import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Tv, ChevronRight } from 'lucide-react';
import { fetchScores, formatGameTime } from '../../lib/scoresApi';
import { TEAM_META } from './ArticleCard';

const SNAPSHOT_TEAMS = ['commanders', 'nationals', 'capitals', 'wizards', 'mystics', 'dcunited'];

const dcSide = (g) => (g.home.is_dc ? g.home : g.away);
const oppSide = (g) => (g.home.is_dc ? g.away : g.home);

const resultLetter = (g) => {
  const dc = dcSide(g), opp = oppSide(g);
  if (dc.score == null || opp.score == null) return '';
  if (Number(dc.score) > Number(opp.score)) return 'W';
  if (Number(dc.score) < Number(opp.score)) return g.league === 'NHL' && /OT|SO/i.test(g.status_detail || '') ? 'OT' : 'L';
  return g.league === 'MLS' ? 'D' : 'T';
};

export const TeamSnapshot = ({ teamKey }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (SNAPSHOT_TEAMS.includes(teamKey)) fetchScores().then(setData);
  }, [teamKey]);

  if (!data || !data.available) return null;
  const team = data.teams?.[teamKey];
  if (!team) return null;

  const meta = TEAM_META[teamKey] || {};
  const accent = meta.color === '#000000' ? '#BF0A30' : meta.color;
  const games = (data.games || []).filter((g) => g.team_key === teamKey);
  const live = games.find((g) => g.status === 'LIVE');
  const next = live || [...games]
    .filter((g) => ['UPCOMING', 'DELAYED'].includes(g.status) && new Date(g.date) > new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date))[0];
  const recent = [...games]
    .filter((g) => g.status === 'FINAL')
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);

  if (!team.record && !next && recent.length === 0) return null;

  // Next series (MLB): consecutive upcoming games vs same opponent
  const upcomingAll = [...games]
    .filter((g) => ['UPCOMING', 'DELAYED'].includes(g.status) && new Date(g.date) > new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  let series = null;
  if (team.league === 'MLB' && upcomingAll.length > 1) {
    const first = upcomingAll[0];
    const run = [first];
    for (let i = 1; i < upcomingAll.length; i++) {
      if (oppSide(upcomingAll[i]).abbr === oppSide(first).abbr && upcomingAll[i].home.is_dc === first.home.is_dc) run.push(upcomingAll[i]);
      else break;
    }
    if (run.length > 1) {
      const fmt = (d) => new Date(d).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' });
      series = {
        opponent: oppSide(first).name,
        home: first.home.is_dc,
        dates: `${fmt(run[0].date)}–${fmt(run[run.length - 1].date)}`,
        games: run.length,
      };
    }
  }

  return (
    <div data-testid={`team-snapshot-${teamKey}`} className="bg-white border border-slate-200">
      <div className="px-4 py-3 flex items-center justify-between" style={{ backgroundColor: accent }}>
        <h3 className="font-heading text-base text-white uppercase tracking-wide">
          {team.name}: Right Now
        </h3>
        <span className="text-white/80 text-xs font-bold uppercase">{team.league}</span>
      </div>

      {/* Record + standing */}
      {(team.record || team.standing) && (
        <div className="px-4 py-3 border-b border-slate-100 flex items-baseline gap-3">
          {team.record && (
            <span data-testid="snapshot-record" className="font-heading text-2xl text-slate-900">{team.record}</span>
          )}
          {team.standing && (
            <span data-testid="snapshot-standing" className="text-xs font-bold uppercase tracking-wide text-slate-500">{team.standing}</span>
          )}
        </div>
      )}

      {/* Next / Live game */}
      {next && (
        <div data-testid="snapshot-next-game" className="px-4 py-3 border-b border-slate-100">
          <p className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-1.5 ${live ? 'text-[#BF0A30]' : 'text-slate-400'}`}>
            {live ? '● Live Now' : 'Next Game'}
          </p>
          <p className="text-sm text-slate-900">
            <span className="font-bold">{next.home.is_dc ? 'vs' : 'at'} {oppSide(next).name}</span>
            {oppSide(next).record && <span className="text-slate-400 text-xs"> ({oppSide(next).record})</span>}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {live ? next.status_detail : formatGameTime(next.date)}
          </p>
          <div className="flex flex-wrap gap-x-3 mt-1">
            {next.venue && (
              <span className="flex items-center gap-1 text-[11px] text-slate-400"><MapPin className="w-3 h-3" />{next.venue}</span>
            )}
            {next.broadcast && (
              <span className="flex items-center gap-1 text-[11px] text-slate-400"><Tv className="w-3 h-3" />{next.broadcast}</span>
            )}
          </div>
        </div>
      )}

      {/* Next series (MLB) */}
      {series && (
        <div data-testid="snapshot-next-series" className="px-4 py-3 border-b border-slate-100">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Next Series</p>
          <p className="text-sm text-slate-900">
            <span className="font-bold">{series.home ? 'vs' : 'at'} {series.opponent}</span>
            <span className="text-slate-500 text-xs"> · {series.games} games · {series.dates}</span>
          </p>
        </div>
      )}

      {/* Recent results */}
      {recent.length > 0 && (
        <div data-testid="snapshot-recent" className="px-4 py-3">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
              Last {recent.length} Games
            </p>
            <div data-testid="snapshot-form" className="flex gap-1">
              {[...recent].reverse().map((g) => {
                const letter = resultLetter(g);
                return (
                  <span
                    key={g.id}
                    className={`min-w-[18px] h-[18px] px-0.5 flex items-center justify-center text-[10px] font-bold text-white ${
                      letter === 'W' ? 'bg-green-700' : letter === 'D' || letter === 'T' || letter === 'OT' ? 'bg-slate-500' : 'bg-red-700'
                    }`}
                  >
                    {letter}
                  </span>
                );
              })}
            </div>
          </div>
          <div className="divide-y divide-slate-50">
            {recent.map((g) => {
              const letter = resultLetter(g);
              return (
                <div key={g.id} className="flex items-center gap-2 py-1">
                  <span className={`w-4 text-xs font-bold ${letter === 'W' ? 'text-green-700' : letter === 'L' ? 'text-red-700' : 'text-slate-500'}`}>
                    {letter}
                  </span>
                  <span className="text-xs text-slate-700 truncate flex-1">
                    {g.home.is_dc ? 'vs' : 'at'} {oppSide(g).abbr}
                  </span>
                  <span className="text-xs tabular-nums text-slate-900 font-semibold">
                    {dcSide(g).score}-{oppSide(g).score}
                  </span>
                  <span className="text-[10px] text-slate-400 w-14 text-right">
                    {new Date(g.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Link
        to="/scores"
        className="flex items-center justify-center gap-1 py-2 border-t border-slate-100 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 hover:text-[#BF0A30] transition-colors"
      >
        Full Scores & Schedule <ChevronRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
};
